import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, Maximize2, Minimize2 } from 'lucide-react';
import { meta } from '../../phases.js';
import { clock, Spinner } from '../../ui/kit.js';

// The session log as a contained instrument (§1.4): its own scroll frame
// with a sticky header, Claude's words as the primary stream, commands and
// tool output as collapsed one-line stubs that expand on tap, timestamps,
// the running cost always visible, auto-follow with a jump pill when you
// scroll up, and one tap to full screen. The DOM stays bounded: the last
// window renders; "Show earlier" widens it a page at a time.
//
// V6 §B: an entry's time is the time the DAEMON recorded it, carried on
// every frame including the ones a reconnect replays. It used to be the
// moment the browser received the frame, which is why scrolling up through
// a long job showed twenty commands all wearing the same minute. And the
// history now arrives newest-first from the server, so "Show earlier"
// fetches another page rather than widening a window over rows the panel
// never had: a job with more than one page used to OPEN on its first
// minutes, because the fetch asked for the oldest 400.

export interface LogEvent {
  id: number;
  ts: string;
  kind: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any;
}

const WINDOW = 250;

export function LogPanel(props: {
  events: LogEvent[];
  delta: string;
  costUsd: number | null;
  live: boolean;
  /** Rows on the server before the oldest one loaded. */
  earlier: number;
  loadingEarlier: boolean;
  onLoadEarlier: () => void;
  error: string | null;
  onRetry: () => void;
}) {
  const [full, setFull] = useState(false);
  const [windowSize, setWindowSize] = useState(WINDOW);
  const [following, setFollowing] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  const visible = useMemo(
    () => (props.events.length > windowSize ? props.events.slice(-windowSize) : props.events),
    [props.events, windowSize],
  );
  const hidden = props.events.length - visible.length;

  // Auto-follow: stick to the tail unless the reader scrolled up.
  useEffect(() => {
    const el = scrollRef.current;
    if (el && following) el.scrollTop = el.scrollHeight;
  }, [props.events.length, props.delta, following, full]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    setFollowing(nearBottom);
  };

  useEffect(() => {
    if (!full) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFull(false);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [full]);

  return (
    <div className={`ops-log${full ? ' ops-log--full' : ''}`} data-testid="log-panel">
      <div className="ops-log__frame">
        <b>Session log</b>
        <span>
          {props.events.length} {props.events.length === 1 ? 'entry' : 'entries'}
        </span>
        {props.live ? <span aria-hidden className="ops-badge__pulse" style={{ background: 'var(--ops-log-run)' }} /> : null}
        {/* A cost that has not been measured says so. Printing $0.00 for
            "no turn has reported yet" is how a job that spent $416 read as
            free (V6 §C). */}
        <span className="ops-log__cost" data-testid="log-cost">
          {props.costUsd != null ? `$${props.costUsd.toFixed(2)}` : 'cost not in yet'}
        </span>
        <button
          type="button"
          className="ops-log__frame-btn"
          aria-label={full ? 'Exit full screen' : 'Full screen'}
          onClick={() => setFull((v) => !v)}
        >
          {full ? <Minimize2 /> : <Maximize2 />}
        </button>
      </div>
      <div className="ops-log__scroll" ref={scrollRef} onScroll={onScroll} data-testid="log-scroll">
        {props.error ? (
          <div className="ops-log__error" data-testid="log-error" role="alert">
            {props.error}
            <button type="button" onClick={props.onRetry}>
              Try again
            </button>
          </div>
        ) : null}
        {/* ONE control for going back, not two. The panel keeps a bounded
            window over what it has loaded and the server holds the rest, so
            "earlier" means widen the window until it runs out and then
            fetch the next page — a person should not have to learn the
            difference, and should not meet two buttons that both say
            earlier (V6 section B). The label says which it is about to do
            and the count is the server's. */}
        {hidden > 0 || props.earlier > 0 ? (
          <button
            type="button"
            className="ops-log__more"
            data-testid="log-earlier"
            disabled={props.loadingEarlier}
            onClick={() => (hidden > 0 ? setWindowSize((w) => w + WINDOW) : props.onLoadEarlier())}
          >
            {props.loadingEarlier ? <Spinner /> : null}
            {props.loadingEarlier
              ? ' Fetching…'
              : `Show ${Math.min(hidden || props.earlier, WINDOW)} earlier ` +
                `(${hidden + props.earlier} before this)`}
          </button>
        ) : null}
        {/* A designed empty state, not three hundred pixels of black. A job
            that has said nothing yet is an ordinary state and the panel
            should say which one it is. */}
        {!props.events.length && !props.delta && !props.error ? (
          <p className="ops-log__empty" data-testid="log-empty">
            {props.live ? 'The session has not said anything yet.' : 'This job produced no session log.'}
          </p>
        ) : null}
        {visible.map((e) => (
          <LogLine key={e.id} event={e} />
        ))}
        {props.delta ? <div className="ops-log__entry ops-log__entry--delta">{props.delta}</div> : null}
        {!following ? (
          <button type="button" className="ops-log__jump" data-testid="log-jump" onClick={() => setFollowing(true)}>
            <ArrowDown /> Latest
          </button>
        ) : null}
      </div>
    </div>
  );
}

function LogLine({ event }: { event: LogEvent }) {
  const p = event.payload ?? {};
  const when = <span className="ops-log__when">{clock(event.ts)}</span>;
  switch (event.kind) {
    case 'assistant_text':
      return (
        <div className="ops-log__entry ops-log__entry--assistant">
          {when}
          {p.text}
        </div>
      );
    case 'tool_use':
      return <Stub label={p.name ?? 'tool'} text={p.summary ?? ''} ts={event.ts} />;
    case 'tool_result': {
      const text = String(p.text ?? '');
      if (p.isError) {
        return (
          <div className="ops-log__entry ops-log__entry--result-error">
            {when}
            {text.slice(0, 600)}
          </div>
        );
      }
      return <Stub label="out" text={text} ts={event.ts} full={text} />;
    }
    case 'followup':
      return (
        <div className="ops-log__entry ops-log__entry--followup">
          {when}→ {p.text}
        </div>
      );
    case 'phase':
      return (
        <div className="ops-log__entry ops-log__entry--system">
          {when}— {meta(p.phase).label.toLowerCase()} —
        </div>
      );
    case 'cost':
      return null;
    case 'artifact':
      return (
        <div className="ops-log__entry ops-log__entry--system">
          {when}📦 {p.file}
        </div>
      );
    case 'system':
      return (
        <div className="ops-log__entry ops-log__entry--system">
          {when}
          {p.note}
        </div>
      );
    case 'shot':
      return (
        <div className="ops-log__entry ops-log__entry--system">
          {when}📸 {p.file ?? `${p.count} CI shots`}
        </div>
      );
    default:
      return null;
  }
}

// A collapsed machine row: one line until tapped. Deliberately a passive
// log line, not a control: at log density a 44px floor would drown the
// narrative, so expansion is a pointer affordance (the terminal pattern)
// and the full text also lives in the expanded row itself.
function Stub(props: { label: string; text: string; ts: string; full?: string }) {
  const [open, setOpen] = useState(false);
  const oneLine = props.text.replace(/\s+/g, ' ').slice(0, 160);
  const expandable = props.full !== undefined;
  const toggle = () => expandable && setOpen((v) => !v);
  if (open && expandable) {
    return (
      <div
        className="ops-log__entry ops-log__entry--result"
        style={{ cursor: 'pointer' }}
        onClick={() => setOpen(false)}
      >
        <span className="ops-log__when">{clock(props.ts)}</span>
        {props.full!.slice(0, 2000)}
      </div>
    );
  }
  return (
    <div
      className="ops-log__stub"
      data-kind="stub"
      style={expandable ? { cursor: 'pointer' } : undefined}
      onClick={toggle}
    >
      <b>{props.label}</b>
      <span>{oneLine || '·'}</span>
      {/* A command carried its timestamp as a prop and never rendered it,
          so the one kind of entry a person scrolls back through looking for
          "when did that run" was the one kind with no time on it (V6
          section B). Last in the row and pushed right, because the stub is
          a flex line and `float` does nothing inside one. */}
      <span className="ops-log__when ops-log__when--inline">{clock(props.ts)}</span>
    </div>
  );
}
